import { Search } from 'lucide-react';
import type { InputHTMLAttributes, SelectHTMLAttributes } from 'react';
import { useId } from 'react';

/*
 * Hai ô lọc dùng chung: ô tìm và ô chọn.
 *
 * `FilterBar` — cái thẻ viền-nền-padding-16 từng bọc chúng — **đã xoá** ở bước
 * 3/3 phần D. `ToolbarBang` thay nó: cùng một hàng lọc nhưng không thẻ, nên
 * thanh lọc cao đúng một hàng nút thay vì 72px, và nó không trông như một khối
 * nội dung thứ hai ngay trên bảng.
 */

export interface OTimKiemProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  value: string;
  onChange: (giaTri: string) => void;
  label?: string;
}

export function OTimKiem({
  value,
  onChange,
  label = 'Tìm kiếm',
  placeholder = 'Tìm kiếm…',
  className = '',
  ...rest
}: OTimKiemProps) {
  const id = useId();
  return (
    <div className={`relative min-w-[220px] flex-1 ${className}`}>
      <label htmlFor={id} className="sr-only">
        {label}
      </label>
      <Search
        size={18}
        className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-chu-mo"
        aria-hidden="true"
      />
      <input
        id={id}
        type="search"
        value={value}
        placeholder={placeholder}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        {...rest}
        className="vung-bam w-full rounded-[10px] border border-vien bg-be-mat pr-3 pl-10 text-co-ban text-chu placeholder:text-chu-mo"
      />
    </div>
  );
}

export interface OChonProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> {
  label: string;
  value: string;
  onChange: (giaTri: string) => void;
  /**
   * Mục của ô. `nhom` có mặt ở mục nào thì ô gom các mục **liền nhau** cùng
   * `nhom` vào một `optgroup` — danh sách dài (kỳ lăn / từng tháng / từng
   * năm) đọc được bằng mắt thay vì ba chục dòng chảy liền.
   */
  options: { value: string; label: string; nhom?: string }[];
  /** Hiện nhãn phía trên thay vì chỉ cho trình đọc màn hình. */
  hienNhan?: boolean;
}

/** Gom các mục liền nhau cùng `nhom`; mục không có `nhom` đứng rời. */
function gomNhom(
  options: { value: string; label: string; nhom?: string }[],
): { nhom?: string; muc: { value: string; label: string }[] }[] {
  const ra: { nhom?: string; muc: { value: string; label: string }[] }[] = [];

  for (const o of options) {
    const cuoi = ra[ra.length - 1];
    if (cuoi && cuoi.nhom === o.nhom) cuoi.muc.push(o);
    else ra.push(o.nhom === undefined ? { muc: [o] } : { nhom: o.nhom, muc: [o] });
  }

  return ra;
}

export function OChon({
  label,
  value,
  onChange,
  options,
  hienNhan = false,
  className = '',
  ...rest
}: OChonProps) {
  const id = useId();
  return (
    <div className={className}>
      <label
        htmlFor={id}
        className={hienNhan ? 'mb-1 block text-phu font-medium text-chu-phu' : 'sr-only'}
      >
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        {...rest}
        /*
          `max-w-full`: bề ngang tự nhiên của `<select>` bằng **mục dài nhất**
          trong nó, nên ô kỳ (có "Tháng 10/2026", "Toàn thời gian"…) rộng 400px
          và tràn ngang ở khổ 390 — bài `e2e/91` bắt đúng chỗ này. Chật thì cắt
          bằng ba chấm của trình duyệt; danh sách mở ra vẫn đọc đủ.
        */
        className="vung-bam w-full max-w-full rounded-[10px] border border-vien bg-be-mat px-3 text-co-ban text-chu"
      >
        {gomNhom(options).map((g) =>
          g.nhom === undefined ? (
            g.muc.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))
          ) : (
            <optgroup key={g.nhom} label={g.nhom}>
              {g.muc.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </optgroup>
          ),
        )}
      </select>
    </div>
  );
}
